import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Link } from 'wouter';
import { 
  FileText, 
  FolderOpen, 
  Layout, 
  Image, 
  Navigation, 
  Plus,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Upload,
  Search,
  Calendar,
  GripVertical
} from 'lucide-react';
import AdminNavigation from '@/components/AdminNavigation';
import AdminFooter from '@/components/AdminFooter';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';

export default function AdminCMS() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<any>(null);

  // Check if user is admin
  const isAdmin = user?.isAdmin;

  // Existing website pages that can be managed through CMS
  const existingPages = [
    { id: 'landing', title: 'Landing Page', slug: '/', status: 'published', template: 'landing', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'home', title: 'Home', slug: '/home', status: 'published', template: 'dashboard', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'about', title: 'About Us', slug: '/about', status: 'published', template: 'content', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'features', title: 'Features', slug: '/features', status: 'published', template: 'features', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'pricing', title: 'Pricing', slug: '/pricing', status: 'published', template: 'pricing', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'programs', title: 'Programs', slug: '/programs', status: 'published', template: 'content', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'help', title: 'Help Center', slug: '/help', status: 'published', template: 'help', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'contact', title: 'Contact Us', slug: '/contact', status: 'published', template: 'contact', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'community', title: 'Community', slug: '/community', status: 'published', template: 'community', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'subscribe', title: 'Subscribe', slug: '/subscribe', status: 'published', template: 'subscription', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'gift-subscription', title: 'Gift Subscription', slug: '/gift-subscription', status: 'published', template: 'subscription', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'influencer-program', title: 'Influencer Program', slug: '/snugglecrafters-influencer-program', status: 'published', template: 'content', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'developer-portal', title: 'Developer Portal', slug: '/developer-portal', status: 'published', template: 'developer', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'api-docs', title: 'API Documentation', slug: '/api-docs', status: 'published', template: 'documentation', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'terms', title: 'Terms of Service', slug: '/terms', status: 'published', template: 'legal', updatedAt: new Date().toISOString(), type: 'system' },
    { id: 'privacy', title: 'Privacy Policy', slug: '/privacy', status: 'published', template: 'legal', updatedAt: new Date().toISOString(), type: 'system' }
  ];

  // Fetch CMS data
  const { data: pagesData, isLoading: pagesLoading } = useQuery({
    queryKey: ['/api/admin/cms/pages'],
    enabled: isAdmin
  });
  const cmsPages = (pagesData as any[]) || [];
  
  // Combine existing system pages with CMS pages
  const pages = [...existingPages, ...cmsPages];

  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ['/api/admin/cms/assets'],
    enabled: isAdmin
  });
  const assets = (assetsData as any[]) || [];

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['/api/admin/cms/templates'],
    enabled: isAdmin
  });
  const templates = (templatesData as any[]) || [];

  const { data: navigationData, isLoading: navigationLoading } = useQuery({
    queryKey: ['/api/admin/cms/navigation'],
    enabled: isAdmin
  });
  const navigation = (navigationData as any[]) || [];

  // Mutations
  const createPageMutation = useMutation({
    mutationFn: async (pageData: any) => {
      const response = await apiRequest('POST', '/api/admin/cms/pages', pageData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/pages'] });
      setPageDialogOpen(false);
      setEditingPage(null);
      toast({ title: 'Success', description: 'Page created successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create page', variant: 'destructive' });
    }
  });

  const updatePageMutation = useMutation({
    mutationFn: async ({ id, ...pageData }: any) => {
      const response = await apiRequest('PUT', `/api/admin/cms/pages/${id}`, pageData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/pages'] });
      setPageDialogOpen(false);
      setEditingPage(null);
      toast({ title: 'Success', description: 'Page updated successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update page', variant: 'destructive' });
    }
  });

  const deletePageMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/admin/cms/pages/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/pages'] });
      toast({ title: 'Success', description: 'Page deleted successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete page', variant: 'destructive' });
    }
  });

  // Asset mutations
  const deleteAssetMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/admin/cms/assets/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/assets'] });
      toast({ title: 'Success', description: 'Asset deleted successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete asset', variant: 'destructive' });
    }
  });

  // Template mutations
  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/admin/cms/templates/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/templates'] });
      toast({ title: 'Success', description: 'Template deleted successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete template', variant: 'destructive' });
    }
  });

  // Navigation mutations
  const deleteNavigationMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/admin/cms/navigation/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/navigation'] });
      toast({ title: 'Success', description: 'Navigation item deleted successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete navigation item', variant: 'destructive' });
    }
  });

  const filteredPages = pages.filter((page: any) => 
    page.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-teal-50 to-amber-50 p-4 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <FileText className="h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">You don't have permission to access the content management system.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-cms">
      <AdminNavigation currentPage="Content Management System" />
      
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">
            Manage website content, pages, assets, and navigation
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Pages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600" data-testid="stat-pages-count">
                {pagesLoading ? '...' : pages.length}
              </div>
              <p className="text-xs text-gray-500 mt-1">Published pages</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Image className="h-4 w-4" />
                Assets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-teal-600" data-testid="stat-assets-count">
                {assetsLoading ? '...' : assets.length}
              </div>
              <p className="text-xs text-gray-500 mt-1">Media files</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Layout className="h-4 w-4" />
                Templates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600" data-testid="stat-templates-count">
                {templatesLoading ? '...' : templates.length}
              </div>
              <p className="text-xs text-gray-500 mt-1">Page templates</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Navigation className="h-4 w-4" />
                Navigation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600" data-testid="stat-navigation-count">
                {navigationLoading ? '...' : navigation.length}
              </div>
              <p className="text-xs text-gray-500 mt-1">Menu items</p>
            </CardContent>
          </Card>
        </div>

        {/* CMS Management Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 h-auto p-1">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pages">Pages</TabsTrigger>
            <TabsTrigger value="assets">Assets</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="navigation">Navigation</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Recent Pages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No pages yet</p>
                    <Button className="mt-4" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Page
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    Recent Assets
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-gray-500">
                    <Image className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No assets uploaded</p>
                    <Button className="mt-4" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Upload Asset
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Pages</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search pages..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                      data-testid="input-search-pages"
                    />
                  </div>
                  <Dialog open={pageDialogOpen} onOpenChange={setPageDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="button-new-page">
                        <Plus className="h-4 w-4 mr-2" />
                        New Page
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Create New Page</DialogTitle>
                      </DialogHeader>
                      <PageForm 
                        page={editingPage}
                        onSubmit={(data: any) => editingPage ? updatePageMutation.mutate({ id: editingPage.id, ...data }) : createPageMutation.mutate(data)}
                        isLoading={createPageMutation.isPending || updatePageMutation.isPending}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {pagesLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading pages...</p>
                  </div>
                ) : filteredPages.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold mb-2">
                      {searchTerm ? 'No pages match your search' : 'No pages found'}
                    </h3>
                    <p className="mb-4">
                      {searchTerm ? 'Try a different search term.' : 'Start creating content by adding your first page.'}
                    </p>
                    {!searchTerm && (
                      <Button onClick={() => setPageDialogOpen(true)} data-testid="button-create-first-page">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Your First Page
                      </Button>
                    )}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Template</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPages.map((page: any) => (
                        <TableRow key={page.id} data-testid={`row-page-${page.id}`}>
                          <TableCell className="font-medium">
                            {page.title}
                            {page.type === 'system' && (
                              <Badge variant="outline" className="ml-2 text-xs">System</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-600">{page.slug}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={page.status === 'published' ? 'default' : 'secondary'}
                              data-testid={`badge-status-${page.id}`}
                            >
                              {page.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-600">{page.template || 'Default'}</TableCell>
                          <TableCell className="text-gray-600">
                            {new Date(page.updatedAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`button-actions-${page.id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem data-testid={`action-view-${page.id}`}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setEditingPage(page);
                                    setPageDialogOpen(true);
                                  }}
                                  data-testid={`action-edit-${page.id}`}
                                  disabled={page.type === 'system'}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  {page.type === 'system' ? 'View Details' : 'Edit'}
                                </DropdownMenuItem>
                                {page.type !== 'system' && (
                                  <DropdownMenuItem 
                                    onClick={() => deletePageMutation.mutate(page.id)}
                                    className="text-red-600"
                                    data-testid={`action-delete-${page.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assets Tab */}
          <TabsContent value="assets">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Assets</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search assets..."
                      className="pl-10 w-64"
                      data-testid="input-search-assets"
                    />
                  </div>
                  <Button variant="outline" data-testid="button-new-folder">
                    <FolderOpen className="h-4 w-4 mr-2" />
                    New Folder
                  </Button>
                  <Dialog open={assetDialogOpen} onOpenChange={setAssetDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="button-upload-asset">
                        <Plus className="h-4 w-4 mr-2" />
                        Upload Asset
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Upload Asset</DialogTitle>
                      </DialogHeader>
                      <AssetUploadForm 
                        onClose={() => setAssetDialogOpen(false)} 
                        onSuccess={() => {
                          queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/assets'] });
                          setAssetDialogOpen(false);
                        }}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {assetsLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading assets...</p>
                  </div>
                ) : assets.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Image className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold mb-2">No assets found</h3>
                    <p className="mb-4">Upload images, documents, and other media files.</p>
                    <Button onClick={() => setAssetDialogOpen(true)} data-testid="button-upload-first-asset">
                      <Plus className="h-4 w-4 mr-2" />
                      Upload Your First Asset
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {assets.map((asset: any) => (
                      <div key={asset.id} className="relative group" data-testid={`asset-${asset.id}`}>
                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          {asset.type?.startsWith('image/') ? (
                            <img
                              src={asset.url}
                              alt={asset.filename}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FileText className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="mt-2">
                          <p className="text-sm font-medium truncate" title={asset.filename}>
                            {asset.filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            {asset.size ? `${Math.round(asset.size / 1024)}KB` : 'Unknown size'}
                          </p>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="secondary" size="sm" data-testid={`asset-actions-${asset.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem data-testid={`asset-copy-url-${asset.id}`}>
                                <Eye className="h-4 w-4 mr-2" />
                                Copy URL
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid={`asset-download-${asset.id}`}>
                                <Upload className="h-4 w-4 mr-2" />
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600" 
                                onClick={() => deleteAssetMutation.mutate(asset.id)}
                                data-testid={`asset-delete-${asset.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Page Templates</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search templates..."
                      className="pl-10 w-64"
                      data-testid="input-search-templates"
                    />
                  </div>
                  <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="button-new-template">
                        <Plus className="h-4 w-4 mr-2" />
                        New Template
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Create New Template</DialogTitle>
                      </DialogHeader>
                      <TemplateForm 
                        onClose={() => setTemplateDialogOpen(false)} 
                        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/templates'] })}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {templatesLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading templates...</p>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Layout className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold mb-2">No templates found</h3>
                    <p className="mb-4">Create reusable page layouts and templates.</p>
                    <Button onClick={() => setTemplateDialogOpen(true)} data-testid="button-create-first-template">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((template: any) => (
                      <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-${template.id}`}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-lg">{template.name}</CardTitle>
                              <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`template-actions-${template.id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem data-testid={`template-preview-${template.id}`}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Preview
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`template-edit-${template.id}`}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`template-duplicate-${template.id}`}>
                                  <Plus className="h-4 w-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-red-600" 
                                  onClick={() => deleteTemplateMutation.mutate(template.id)}
                                  data-testid={`template-delete-${template.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="h-4 w-4" />
                              Updated {new Date(template.updatedAt).toLocaleDateString()}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {template.type || 'General'}
                              </Badge>
                              {template.isDefault && (
                                <Badge variant="default" className="text-xs">
                                  Default
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Navigation Tab */}
          <TabsContent value="navigation">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Navigation Management</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search navigation..."
                      className="pl-10 w-64"
                      data-testid="input-search-navigation"
                    />
                  </div>
                  <Dialog open={navDialogOpen} onOpenChange={setNavDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="button-add-menu-item">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Menu Item
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Add Menu Item</DialogTitle>
                      </DialogHeader>
                      <NavigationForm 
                        onClose={() => setNavDialogOpen(false)} 
                        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/navigation'] })}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {navigationLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading navigation...</p>
                  </div>
                ) : navigation.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Navigation className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold mb-2">No navigation items found</h3>
                    <p className="mb-4">Configure your website's navigation menus.</p>
                    <Button onClick={() => setNavDialogOpen(true)} data-testid="button-add-first-menu">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Menu Item
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {navigation.map((item: any, index: number) => (
                      <Card key={item.id} className="p-4" data-testid={`nav-item-${item.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                              <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
                            </div>
                            <div>
                              <h4 className="font-medium">{item.label}</h4>
                              <p className="text-sm text-gray-500">{item.url || item.path}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={item.isVisible ? "default" : "secondary"} className="text-xs">
                              {item.isVisible ? "Visible" : "Hidden"}
                            </Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`nav-actions-${item.id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem data-testid={`nav-edit-${item.id}`}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`nav-duplicate-${item.id}`}>
                                  <Plus className="h-4 w-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`nav-toggle-${item.id}`}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  {item.isVisible ? 'Hide' : 'Show'}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-red-600" 
                                  onClick={() => deleteNavigationMutation.mutate(item.id)}
                                  data-testid={`nav-delete-${item.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        {item.children && item.children.length > 0 && (
                          <div className="mt-3 ml-8 space-y-2">
                            {item.children.map((child: any) => (
                              <div key={child.id} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-testid={`nav-child-${child.id}`}>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400">↳</span>
                                  <span className="text-sm">{child.label}</span>
                                  <span className="text-xs text-gray-500">({child.url || child.path})</span>
                                </div>
                                <Badge variant={child.isVisible ? "default" : "secondary"} className="text-xs">
                                  {child.isVisible ? "Visible" : "Hidden"}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <AdminFooter />
    </div>
  );
}

// PageForm component for creating/editing pages
function PageForm({ page = null, onSubmit, isLoading }: {
  page?: any;
  onSubmit: (data: any) => void;
  isLoading: boolean;
}) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    title: page?.title || '',
    slug: page?.slug || '',
    content: page?.content || '',
    metaTitle: page?.metaTitle || '',
    metaDescription: page?.metaDescription || '',
    status: page?.status || 'draft',
    template: page?.template || 'default'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: !page ? generateSlug(title) : prev.slug // Only auto-generate for new pages
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="title">Page Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={handleTitleChange}
          placeholder="Enter page title"
          required
          data-testid="input-page-title"
        />
      </div>

      <div>
        <Label htmlFor="slug">URL Slug *</Label>
        <Input
          id="slug"
          value={formData.slug}
          onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
          placeholder="page-url-slug"
          required
          data-testid="input-page-slug"
        />
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select 
          value={formData.status} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
        >
          <SelectTrigger data-testid="select-page-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="content">Content</Label>
        <Textarea
          id="content"
          value={formData.content}
          onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
          placeholder="Enter page content..."
          rows={6}
          data-testid="textarea-page-content"
        />
      </div>

      <div>
        <Label htmlFor="metaTitle">Meta Title (SEO)</Label>
        <Input
          id="metaTitle"
          value={formData.metaTitle}
          onChange={(e) => setFormData(prev => ({ ...prev, metaTitle: e.target.value }))}
          placeholder="SEO title for this page"
          data-testid="input-page-meta-title"
        />
      </div>

      <div>
        <Label htmlFor="metaDescription">Meta Description (SEO)</Label>
        <Textarea
          id="metaDescription"
          value={formData.metaDescription}
          onChange={(e) => setFormData(prev => ({ ...prev, metaDescription: e.target.value }))}
          placeholder="SEO description for this page"
          rows={3}
          data-testid="textarea-page-meta-description"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" disabled={isLoading} data-testid="button-save-page">
          {isLoading ? 'Saving...' : page ? 'Update Page' : 'Create Page'}
        </Button>
      </div>
    </form>
  );
}

// AssetUploadForm component for uploading files
function AssetUploadForm({ onClose, onSuccess }: { onClose: () => void; onSuccess?: () => void }) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const uploadAssetMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await fetch('/api/admin/cms/assets', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/assets'] });
      onSuccess?.();
      onClose();
      toast({ title: 'Success', description: 'Asset uploaded successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to upload asset', variant: 'destructive' });
    }
  });

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file.size > maxSize) {
      toast({ 
        title: 'Error', 
        description: 'File size must be less than 10MB', 
        variant: 'destructive' 
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    formData.append('type', file.type);
    formData.append('size', file.size.toString());

    setUploading(true);
    uploadAssetMutation.mutate(formData);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  return (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-teal-500 bg-teal-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        data-testid="asset-drop-zone"
      >
        <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-medium mb-2">Drop files here or click to upload</p>
        <p className="text-sm text-gray-500 mb-4">
          Support for images, documents, and other files up to 10MB
        </p>
        <input
          type="file"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          id="file-input"
          accept="image/*,application/pdf,.doc,.docx,.txt"
          data-testid="file-input"
        />
        <Button 
          type="button" 
          variant="outline"
          onClick={() => document.getElementById('file-input')?.click()}
          disabled={uploading}
          data-testid="button-select-file"
        >
          {uploading ? 'Uploading...' : 'Select File'}
        </Button>
      </div>

      {uploading && (
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
          <span className="text-sm text-gray-600">Uploading...</span>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={uploading} data-testid="button-cancel-upload">
          Cancel
        </Button>
      </div>
    </div>
  );
}

// TemplateForm component for creating/editing templates
function TemplateForm({ onClose, onSuccess }: { onClose: () => void; onSuccess?: () => void }) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    type: 'general',
    isDefault: false
  });

  const createTemplateMutation = useMutation({
    mutationFn: async (templateData: any) => {
      const response = await apiRequest('POST', '/api/admin/cms/templates', templateData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/templates'] });
      onSuccess?.();
      onClose();
      toast({ title: 'Success', description: 'Template created successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create template', variant: 'destructive' });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createTemplateMutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="template-name">Template Name *</Label>
        <Input
          id="template-name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter template name"
          required
          data-testid="input-template-name"
        />
      </div>

      <div>
        <Label htmlFor="template-description">Description</Label>
        <Textarea
          id="template-description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe this template's purpose"
          rows={3}
          data-testid="textarea-template-description"
        />
      </div>

      <div>
        <Label htmlFor="template-type">Template Type</Label>
        <Select 
          value={formData.type} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
        >
          <SelectTrigger data-testid="select-template-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="blog">Blog Post</SelectItem>
            <SelectItem value="landing">Landing Page</SelectItem>
            <SelectItem value="product">Product Page</SelectItem>
            <SelectItem value="contact">Contact Page</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="template-content">Template Content</Label>
        <Textarea
          id="template-content"
          value={formData.content}
          onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
          placeholder="Enter template HTML/content structure..."
          rows={8}
          data-testid="textarea-template-content"
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="template-default"
          checked={formData.isDefault}
          onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
          data-testid="checkbox-template-default"
        />
        <Label htmlFor="template-default">Set as default template</Label>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onClose}
          data-testid="button-cancel-template"
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={createTemplateMutation.isPending}
          data-testid="button-save-template"
        >
          {createTemplateMutation.isPending ? 'Creating...' : 'Create Template'}
        </Button>
      </div>
    </form>
  );
}

// NavigationForm component for creating/editing navigation items
function NavigationForm({ onClose, onSuccess }: { onClose: () => void; onSuccess?: () => void }) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    label: '',
    url: '',
    path: '',
    type: 'internal',
    parentId: '',
    isVisible: true,
    order: 0
  });

  const createNavigationMutation = useMutation({
    mutationFn: async (navData: any) => {
      const response = await apiRequest('POST', '/api/admin/cms/navigation', navData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cms/navigation'] });
      onSuccess?.();
      onClose();
      toast({ title: 'Success', description: 'Navigation item added successfully!' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to add navigation item', variant: 'destructive' });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createNavigationMutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="nav-label">Menu Label *</Label>
        <Input
          id="nav-label"
          value={formData.label}
          onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
          placeholder="Enter menu label"
          required
          data-testid="input-nav-label"
        />
      </div>

      <div>
        <Label htmlFor="nav-type">Link Type</Label>
        <Select 
          value={formData.type} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
        >
          <SelectTrigger data-testid="select-nav-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="internal">Internal Page</SelectItem>
            <SelectItem value="external">External URL</SelectItem>
            <SelectItem value="dropdown">Dropdown Menu</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {formData.type === 'internal' && (
        <div>
          <Label htmlFor="nav-path">Internal Path</Label>
          <Input
            id="nav-path"
            value={formData.path}
            onChange={(e) => setFormData(prev => ({ ...prev, path: e.target.value }))}
            placeholder="/about, /contact, etc."
            data-testid="input-nav-path"
          />
        </div>
      )}

      {formData.type === 'external' && (
        <div>
          <Label htmlFor="nav-url">External URL</Label>
          <Input
            id="nav-url"
            value={formData.url}
            onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
            placeholder="https://example.com"
            data-testid="input-nav-url"
          />
        </div>
      )}

      <div>
        <Label htmlFor="nav-order">Display Order</Label>
        <Input
          id="nav-order"
          type="number"
          value={formData.order}
          onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
          placeholder="0"
          data-testid="input-nav-order"
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="nav-visible"
          checked={formData.isVisible}
          onChange={(e) => setFormData(prev => ({ ...prev, isVisible: e.target.checked }))}
          data-testid="checkbox-nav-visible"
        />
        <Label htmlFor="nav-visible">Visible in navigation</Label>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onClose}
          data-testid="button-cancel-nav"
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={createNavigationMutation.isPending}
          data-testid="button-save-nav"
        >
          {createNavigationMutation.isPending ? 'Adding...' : 'Add Menu Item'}
        </Button>
      </div>
    </form>
  );
}